import { useMemo } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Table, type Column } from "@/components/ui/Table";
import {
  formatDateDe,
  formatDurationHours,
  getStatusInfo,
  type HeroUsageRow,
} from "@/lib/auswertungen";

interface KeyedRow extends HeroUsageRow {
  __key: string;
}

const COLUMNS: Column<KeyedRow>[] = [
  {
    key: "work_date",
    header: "Datum",
    sortable: true,
    sortValue: (row) => row.work_date,
    render: (row) => <span className="whitespace-nowrap">{formatDateDe(row.work_date)}</span>,
  },
  {
    key: "partner_name",
    header: "Person",
    sortable: true,
    sortValue: (row) => row.partner_name || "",
    render: (row) => row.partner_name || "",
  },
  {
    key: "duration",
    header: "Dauer",
    sortable: true,
    sortValue: (row) => Number(row.duration_in_seconds ?? row.duration ?? 0),
    render: (row) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatDurationHours(Number(row.duration_in_seconds ?? row.duration ?? 0))}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    sortValue: (row) => getStatusInfo(row).status,
    render: (row) => getStatusInfo(row).status,
  },
  {
    key: "auftragsart",
    header: "Auftragsart",
    sortable: true,
    sortValue: (row) => row.auftragsart ?? "",
    render: (row) => row.auftragsart ?? "–",
  },
  {
    key: "comment",
    header: "Kommentar",
    sortable: true,
    sortValue: (row) => row.comment ?? "",
    render: (row) => row.comment ?? "",
  },
  {
    key: "projekt",
    header: "Projekt",
    sortable: true,
    sortValue: (row) => row.projekt?.trim() || "",
    render: (row) => (
      <ProjectOverviewLink
        auftragId={row.auftrag_id}
        label={row.projekt?.trim() || "–"}
      />
    ),
  },
];

export function HeroUsageTable({
  rows,
  emptyText = "Keine Einträge gefunden.",
  stickyHeader = true,
}: {
  rows: HeroUsageRow[];
  emptyText?: string;
  stickyHeader?: boolean;
}) {
  const keyedRows = useMemo<KeyedRow[]>(
    () => rows.map((row, index) => ({ ...row, __key: `${row.hero_uuid ?? "row"}:${index}` })),
    [rows],
  );

  return (
    <Table<KeyedRow>
      columns={COLUMNS}
      rows={keyedRows}
      rowKey={(row) => row.__key}
      emptyText={emptyText}
      stickyHeader={stickyHeader}
      rowClassName={(row) => `eh-report-row--${getStatusInfo(row).classState}`}
    />
  );
}
